import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id: slug } = useParams<{ id: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-20 w-full px-4 md:px-6 lg:px-8 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
          <Link to="/" className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    url: `https://17c.com/article/${article.slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://17c.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `https://17c.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 17c.com`}
        description={article.excerpt}
        keywords={`17c.com,${article.categoryName},${article.title}`}
        canonicalUrl={`https://17c.com/article/${article.slug}`}
        ogType="article"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: article.categoryName, path: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-foreground-500 text-base leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-12 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200/70 pt-10 mt-10">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-5">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                    >
                      <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-600 transition-colors truncate-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 leading-relaxed truncate-2">
                        {rel.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-background-200/70 pt-8 mt-8 text-center">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i> 返回{article.categoryName}列表
              </Link>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}